// Admin configuration screen for work phases — the customer's own vocabulary
// for the KIND of work a labor phase is. A Holman job order says
// "100 Installation", "700 Service", "900 Inspection"; a pharma plant says
// granulation / compression / QA; a food line says setup / production / washup.
//
// Each phase carries a DEFAULT JOB ROLE, and that is the point: a job-order PDF
// whose phase codes are configured here maps itself on import instead of asking
// the user to bind phase -> job role every single time.
//
// Mounted beside the work-order attributes panel under the Setup tab.
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import {
	Alert,
	Box,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { JobRole, jobsApi } from "../../../services/jobs-api";
import {
	UpdateWorkPhaseDto,
	WorkPhase, // eslint-disable-line sort-imports
	workPhasesApi,
	WorkPhaseTemplate, // eslint-disable-line sort-imports
	WorkPhaseTemplatePhase,
} from "../../../services/work-phases-api";
import {
	EmptyState,
	GhostButton,
	MonoText,
	planner,
	PlannerCard, // eslint-disable-line sort-imports
	PlannerHeader,
	PlannerPage,
	PrimaryButton,
} from "../planner-ui";

// ---- Design-system styling (see planner-ui/tokens.ts) ----

// Every control in this screen: hairline rest state, small radius, brick focus.
const fieldSx = {
	bgcolor: planner.surface,
	"& .MuiOutlinedInput-root": {
		fontFamily: planner.fontUi,
		borderRadius: `${planner.radiusSm}px`,
		"& fieldset": { borderColor: planner.hairlineStrong },
		"&:hover fieldset": { borderColor: planner.inkMuted },
		"&.Mui-focused fieldset": {
			borderColor: planner.accent,
			borderWidth: `${planner.borderWidth}px`,
		},
	},
	"& .MuiInputLabel-root": {
		fontFamily: planner.fontUi,
		color: planner.inkSecondary,
		"&.Mui-focused": { color: planner.accent },
	},
	"& .MuiFormHelperText-root": {
		...planner.type.meta,
		fontFamily: planner.fontUi,
		color: planner.inkMuted,
	},
} as const;

// Small pill used for the default role / sort order metadata on each row.
const metaPillSx = {
	...planner.type.metaStrong,
	fontFamily: planner.fontUi,
	px: 1,
	py: 0.25,
	borderRadius: `${planner.radiusSm}px`,
	whiteSpace: "nowrap" as const,
} as const;

const dialogPaperSx = {
	borderRadius: `${planner.radius}px`,
	boxShadow: planner.shadowDialog,
	fontFamily: planner.fontUi,
};

const dialogTitleSx = {
	...planner.type.pageTitle,
	color: planner.navy,
	bgcolor: planner.surface,
};

const alertSx = (fill: string, soft: string) => ({
	borderRadius: `${planner.radiusSm}px`,
	bgcolor: soft,
	border: `1px solid ${fill}33`,
	color: planner.ink,
	"& .MuiAlert-icon": { color: fill },
	"& .MuiAlert-message": { ...planner.type.body, fontFamily: planner.fontUi },
});

interface EditorState {
	code: string;
	name: string;
	description: string;
	defaultJobRoleId: number | "";
}

const EMPTY_EDITOR: EditorState = {
	code: "",
	name: "",
	description: "",
	defaultJobRoleId: "",
};

// A template suggests a role by NAME; the company's real job roles are the only
// thing that can satisfy it. Exact match first, then a contained-word match, so
// "Mechanical" finds "Mechanical Technician".
const resolveSuggestedRole = (
	suggestedName: string | undefined,
	roles: JobRole[]
): JobRole | undefined => {
	if (!suggestedName) return undefined;
	const wanted = suggestedName.trim().toLowerCase();
	if (!wanted) return undefined;
	return (
		roles.find((role) => role.name.trim().toLowerCase() === wanted) ||
		roles.find((role) => role.name.toLowerCase().includes(wanted)) ||
		roles.find((role) => wanted.includes(role.name.toLowerCase()))
	);
};

const toUpdateDto = (phase: WorkPhase): UpdateWorkPhaseDto => ({
	id: phase.id,
	companyId: phase.companyId,
	code: phase.code,
	name: phase.name,
	description: phase.description,
	defaultJobRoleId: phase.defaultJobRoleId ?? null,
	sortOrder: phase.sortOrder,
	isActive: phase.isActive,
});

// The server answers 409 while a labor standard still references the phase.
// That reason is the whole point of the message — never replace it with a
// generic failure line.
const describeError = (error: any, fallback: string): string =>
	error?.response?.data?.message ||
	error?.response?.data?.title ||
	(typeof error?.response?.data === "string" ? error.response.data : "") ||
	fallback;

const WorkPhasesAdmin: React.FC = () => {
	const companyId = useSelector((state: any) => state?.user?.userData?.companyId);
	const userId = useSelector((state: any) => state?.user?.userData?.id);

	const [phases, setPhases] = useState<WorkPhase[]>([]);
	const [loading, setLoading] = useState(false);
	const [jobRoles, setJobRoles] = useState<JobRole[]>([]);
	const [templates, setTemplates] = useState<WorkPhaseTemplate[]>([]);
	const [showTemplatePicker, setShowTemplatePicker] = useState(false);
	const [applyingTemplateKey, setApplyingTemplateKey] = useState<string | null>(null);
	const [editorOpen, setEditorOpen] = useState(false);
	const [editingPhase, setEditingPhase] = useState<WorkPhase | null>(null);
	const [editor, setEditor] = useState<EditorState>(EMPTY_EDITOR);
	const [editorError, setEditorError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [deletingPhase, setDeletingPhase] = useState<WorkPhase | null>(null);
	const [deleteError, setDeleteError] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity?: "success" | "error" }>({
		open: false,
		message: "",
	});

	const roleById = useMemo(() => {
		const map = new Map<number, JobRole>();
		jobRoles.forEach((role) => map.set(role.id, role));
		return map;
	}, [jobRoles]);

	const loadPhases = useCallback(async () => {
		if (!companyId) return;
		setLoading(true);
		try {
			const data = await workPhasesApi.getByCompany(companyId);
			setPhases(data);
		} catch (error: any) {
			console.error("Failed to load work phases:", error);
			setSnackbar({
				open: true,
				message: describeError(error, "Failed to load work phases"),
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	}, [companyId]);

	useEffect(() => {
		loadPhases();
	}, [loadPhases]);

	// The company's real job roles — the same endpoint the labor-standards page
	// and the attributes panel's siblings use.
	useEffect(() => {
		if (!userId) return;
		jobsApi
			.getAll(String(userId))
			.then((data) => setJobRoles([...(data || [])].sort((a, b) => a.name.localeCompare(b.name))))
			.catch((error) => console.error("Failed to load job roles:", error));
	}, [userId]);

	const roleLabel = useCallback(
		(phase: WorkPhase): string => {
			if (phase.defaultJobRole) return phase.defaultJobRole;
			if (phase.defaultJobRoleId) {
				return roleById.get(phase.defaultJobRoleId)?.name || `Role ${phase.defaultJobRoleId}`;
			}
			return "";
		},
		[roleById]
	);

	const handleOpenTemplatePicker = useCallback(async () => {
		setShowTemplatePicker(true);
		if (templates.length === 0) {
			try {
				const data = await workPhasesApi.getTemplates();
				setTemplates(data);
			} catch (error: any) {
				console.error("Failed to load work phase templates:", error);
				setSnackbar({
					open: true,
					message: describeError(error, "Failed to load starter sets"),
					severity: "error",
				});
			}
		}
	}, [templates.length]);

	// Applying a starter set is server-side and idempotent. The template's
	// suggested roles are names, so afterwards we bind each newly added phase
	// that still has no default role to the company's matching job role — that
	// binding is what makes the next import self-mapping.
	const bindSuggestedRoles = useCallback(
		async (templatePhases: WorkPhaseTemplatePhase[], current: WorkPhase[]) => {
			if (jobRoles.length === 0) return 0;
			const updates: UpdateWorkPhaseDto[] = [];
			templatePhases.forEach((templatePhase) => {
				const saved = current.find(
					(phase) => phase.code.trim().toLowerCase() === templatePhase.code.trim().toLowerCase()
				);
				if (!saved || saved.defaultJobRoleId) return;
				const role = resolveSuggestedRole(templatePhase.suggestedJobRoleName, jobRoles);
				if (!role) return;
				updates.push({ ...toUpdateDto(saved), defaultJobRoleId: role.id });
			});
			if (updates.length === 0) return 0;
			await Promise.all(updates.map((update) => workPhasesApi.update(update.id, update)));
			return updates.length;
		},
		[jobRoles]
	);

	const handleApplyTemplate = useCallback(
		async (template: WorkPhaseTemplate) => {
			if (!companyId) return;
			setApplyingTemplateKey(template.key);
			try {
				const result = await workPhasesApi.applyTemplate(companyId, template.key);
				const applied = await workPhasesApi.getByCompany(companyId);
				const bound = await bindSuggestedRoles(template.phases, applied);
				setPhases(bound > 0 ? await workPhasesApi.getByCompany(companyId) : applied);
				setShowTemplatePicker(false);
				const counts =
					result?.addedCount === undefined
						? `${template.name} applied`
						: `${result.addedCount} added, ${result.skippedCount ?? 0} already present`;
				setSnackbar({
					open: true,
					message: bound > 0 ? `${counts} — ${bound} matched to a job role` : counts,
					severity: "success",
				});
			} catch (error: any) {
				console.error("Failed to apply work phase template:", error);
				setSnackbar({
					open: true,
					message: describeError(error, "Failed to apply the starter set"),
					severity: "error",
				});
			} finally {
				setApplyingTemplateKey(null);
			}
		},
		[companyId, bindSuggestedRoles]
	);

	const handleOpenCreate = useCallback(() => {
		setEditingPhase(null);
		setEditor(EMPTY_EDITOR);
		setEditorError(null);
		setEditorOpen(true);
	}, []);

	const handleOpenEdit = useCallback((phase: WorkPhase) => {
		setEditingPhase(phase);
		setEditor({
			code: phase.code,
			name: phase.name,
			description: phase.description || "",
			defaultJobRoleId: phase.defaultJobRoleId ?? "",
		});
		setEditorError(null);
		setEditorOpen(true);
	}, []);

	const handleSavePhase = useCallback(async () => {
		if (!companyId) return;
		const code = editor.code.trim();
		const name = editor.name.trim();
		if (!code || !name) {
			setEditorError("Code and name are required");
			return;
		}
		const clash = phases.find(
			(phase) => phase.id !== editingPhase?.id && phase.code.trim().toLowerCase() === code.toLowerCase()
		);
		if (clash) {
			setEditorError(`Code "${code}" is already used by "${clash.name}"`);
			return;
		}

		setSaving(true);
		setEditorError(null);
		try {
			const dto = {
				companyId,
				code,
				name,
				description: editor.description.trim() || undefined,
				defaultJobRoleId: editor.defaultJobRoleId === "" ? null : Number(editor.defaultJobRoleId),
				sortOrder: editingPhase
					? editingPhase.sortOrder
					: (phases.length > 0 ? Math.max(...phases.map((phase) => phase.sortOrder)) : 0) + 1,
			};
			if (editingPhase) {
				await workPhasesApi.update(editingPhase.id, {
					...dto,
					id: editingPhase.id,
					isActive: editingPhase.isActive,
				});
			} else {
				await workPhasesApi.create(dto);
			}
			setEditorOpen(false);
			setSnackbar({
				open: true,
				message: editingPhase ? "Work phase updated" : "Work phase added",
				severity: "success",
			});
			await loadPhases();
		} catch (error: any) {
			console.error("Failed to save work phase:", error);
			setEditorError(describeError(error, "Failed to save the work phase"));
		} finally {
			setSaving(false);
		}
	}, [companyId, editor, editingPhase, phases, loadPhases]);

	const handleConfirmDelete = useCallback(async () => {
		if (!deletingPhase) return;
		setDeleting(true);
		setDeleteError(null);
		try {
			await workPhasesApi.delete(deletingPhase.id);
			setPhases((prev) => prev.filter((phase) => phase.id !== deletingPhase.id));
			setSnackbar({ open: true, message: "Work phase deleted", severity: "success" });
			setDeletingPhase(null);
		} catch (error: any) {
			console.error("Failed to delete work phase:", error);
			// 409 means a labor standard still points at it — keep the dialog open
			// and show the server's reason where the user is looking.
			setDeleteError(
				describeError(
					error,
					error?.response?.status === 409
						? "A labor standard still uses this work phase, so it cannot be deleted."
						: "Failed to delete the work phase"
				)
			);
		} finally {
			setDeleting(false);
		}
	}, [deletingPhase]);

	// Reorder via up/down buttons: swap positions, renumber sortOrder 1..n,
	// persist only the rows whose sortOrder changed.
	const handleMove = useCallback(
		async (index: number, delta: -1 | 1) => {
			const target = index + delta;
			if (target < 0 || target >= phases.length) return;
			const reordered = [...phases];
			[reordered[index], reordered[target]] = [reordered[target], reordered[index]];
			const renumbered = reordered.map((phase, position) => ({ ...phase, sortOrder: position + 1 }));
			const changed = renumbered.filter((phase) => {
				const before = phases.find((candidate) => candidate.id === phase.id);
				return before && before.sortOrder !== phase.sortOrder;
			});
			setPhases(renumbered);
			try {
				await Promise.all(changed.map((phase) => workPhasesApi.update(phase.id, toUpdateDto(phase))));
			} catch (error: any) {
				console.error("Failed to reorder work phases:", error);
				setSnackbar({
					open: true,
					message: describeError(error, "Failed to save the new order"),
					severity: "error",
				});
				await loadPhases();
			}
		},
		[phases, loadPhases]
	);

	return (
		<PlannerPage>
			<PlannerHeader
				title="Work phases"
				subtitle="Your own work-type codes for a labor phase — 100 Installation, Granulation, Washup. Each one's default job role is what lets a job-order import map itself."
				actions={
					<>
						<GhostButton startIcon={<AutoAwesomeIcon />} onClick={handleOpenTemplatePicker}>
							Apply a starter set
						</GhostButton>
						<PrimaryButton startIcon={<AddIcon />} onClick={handleOpenCreate}>
							Add work phase
						</PrimaryButton>
					</>
				}
			/>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<CircularProgress size={28} sx={{ color: planner.navy }} />
				</Box>
			) : phases.length === 0 ? (
				<PlannerCard>
					<EmptyState
						icon={<LayersOutlinedIcon sx={{ fontSize: 40, mb: 1, color: planner.inkMuted }} />}
						title="No work phases configured"
						body="A work phase is the customer's name for a kind of work — 100 Installation, 700 Service, 900 Inspection on a vehicle upfit; granulation, compression, QA in a plant. Give each one a default job role and every job-order import maps itself instead of asking. Start from a set below."
						actionLabel="Apply a starter set"
						onAction={handleOpenTemplatePicker}
					/>
				</PlannerCard>
			) : (
				<PlannerCard>
					{phases.map((phase, index) => {
						const defaultRole = roleLabel(phase);
						return (
							<Box
								key={phase.id}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1.5,
									px: 2,
									py: 1.25,
									borderTop: index > 0 ? `1px solid ${planner.hairline}` : "none",
									"&:hover .phase-row-actions": { opacity: 1 },
								}}
							>
								<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
									<IconButton
										size="small"
										disabled={index === 0}
										onClick={() => handleMove(index, -1)}
										sx={{ p: 0.25, color: planner.inkMuted }}
									>
										<ArrowUpwardIcon sx={{ fontSize: 14 }} />
									</IconButton>
									<IconButton
										size="small"
										disabled={index === phases.length - 1}
										onClick={() => handleMove(index, 1)}
										sx={{ p: 0.25, color: planner.inkMuted }}
									>
										<ArrowDownwardIcon sx={{ fontSize: 14 }} />
									</IconButton>
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
										<MonoText sx={{ color: planner.accentDeep, fontSize: 13 }}>{phase.code}</MonoText>
										<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>
											{phase.name}
										</Typography>
									</Box>
									{phase.description ? (
										<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, mt: 0.25 }}>
											{phase.description}
										</Typography>
									) : null}
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
										{defaultRole ? (
											<Box
												component="span"
												sx={{
													...metaPillSx,
													bgcolor: planner.accentSoft,
													border: `1px solid ${planner.accentBorder}`,
													color: planner.accentDeep,
												}}
											>
												{defaultRole}
											</Box>
										) : (
											<Tooltip title="Imports will still ask for a job role until this is set">
												<Box
													component="span"
													sx={{
														...metaPillSx,
														bgcolor: planner.partialSoft,
														border: `1px solid ${planner.partial}33`,
														color: planner.partial,
													}}
												>
													No default role
												</Box>
											</Tooltip>
										)}
										<Box
											component="span"
											sx={{
												...metaPillSx,
												fontFamily: planner.fontMono,
												bgcolor: planner.surfaceSubtle,
												border: `1px solid ${planner.hairline}`,
												color: planner.inkMuted,
												fontVariantNumeric: "tabular-nums",
											}}
										>
											{phase.sortOrder}
										</Box>
									</Box>
								</Box>
								<Box
									className="phase-row-actions"
									sx={{ display: "flex", gap: 0.25, opacity: 0, transition: "opacity 120ms ease", flexShrink: 0 }}
								>
									<Tooltip title="Edit">
										<IconButton
											size="small"
											onClick={() => handleOpenEdit(phase)}
											sx={{ p: 0.5, color: planner.inkMuted, "&:hover": { color: planner.ink } }}
										>
											<EditOutlinedIcon sx={{ fontSize: 16 }} />
										</IconButton>
									</Tooltip>
									<Tooltip title="Delete">
										<IconButton
											size="small"
											onClick={() => {
												setDeleteError(null);
												setDeletingPhase(phase);
											}}
											sx={{ p: 0.5, color: planner.inkMuted, "&:hover": { color: planner.unstaffed } }}
										>
											<DeleteOutlineIcon sx={{ fontSize: 16 }} />
										</IconButton>
									</Tooltip>
								</Box>
							</Box>
						);
					})}
				</PlannerCard>
			)}

			{/* Add / edit work phase dialog */}
			<Dialog
				open={editorOpen}
				onClose={() => setEditorOpen(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ sx: dialogPaperSx }}
			>
				<DialogTitle sx={dialogTitleSx}>
					{editingPhase ? "Edit work phase" : "Add work phase"}
					<IconButton
						onClick={() => setEditorOpen(false)}
						sx={{ position: "absolute", right: 8, top: 8, color: planner.inkMuted }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers sx={{ bgcolor: planner.canvas, borderColor: planner.hairline }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
						{editorError && (
							<Alert severity="error" sx={alertSx(planner.unstaffed, planner.unstaffedSoft)}>
								{editorError}
							</Alert>
						)}
						<TextField
							label="Code"
							value={editor.code}
							onChange={(event) => setEditor((prev) => ({ ...prev, code: event.target.value }))}
							placeholder="e.g. 100"
							helperText="The code as the customer's document prints it — this is what an import matches on"
							fullWidth
							sx={fieldSx}
							InputProps={{ sx: { fontFamily: planner.fontMono, fontSize: 13 } }}
						/>
						<TextField
							label="Name"
							value={editor.name}
							onChange={(event) => setEditor((prev) => ({ ...prev, name: event.target.value }))}
							placeholder="e.g. Installation"
							fullWidth
							sx={fieldSx}
						/>
						<FormControl fullWidth sx={fieldSx}>
							<InputLabel id="work-phase-role-label">Default job role</InputLabel>
							<Select
								labelId="work-phase-role-label"
								label="Default job role"
								value={editor.defaultJobRoleId}
								onChange={(event) =>
									setEditor((prev) => ({
										...prev,
										defaultJobRoleId: event.target.value === "" ? "" : Number(event.target.value),
									}))
								}
							>
								<MenuItem value="" sx={{ fontFamily: planner.fontUi, fontSize: 13 }}>
									None
								</MenuItem>
								{jobRoles.map((role) => (
									<MenuItem key={role.id} value={role.id} sx={{ fontFamily: planner.fontUi, fontSize: 13 }}>
										{role.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted, mt: -1 }}>
							An import that sees this code prefills this role. Leave it empty and the import will keep
							asking.
						</Typography>
						<TextField
							label="Description (optional)"
							value={editor.description}
							onChange={(event) => setEditor((prev) => ({ ...prev, description: event.target.value }))}
							placeholder="What this phase covers"
							fullWidth
							multiline
							minRows={2}
							sx={fieldSx}
						/>
					</Box>
				</DialogContent>
				<DialogActions sx={{ bgcolor: planner.surface }}>
					<GhostButton onClick={() => setEditorOpen(false)}>Cancel</GhostButton>
					<PrimaryButton onClick={handleSavePhase} disabled={saving}>
						{saving ? "Saving..." : editingPhase ? "Update" : "Add"}
					</PrimaryButton>
				</DialogActions>
			</Dialog>

			{/* Starter-set picker */}
			<Dialog
				open={showTemplatePicker}
				onClose={() => setShowTemplatePicker(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ sx: dialogPaperSx }}
			>
				<DialogTitle sx={dialogTitleSx}>
					Apply a starter set
					<IconButton
						onClick={() => setShowTemplatePicker(false)}
						sx={{ position: "absolute", right: 8, top: 8, color: planner.inkMuted }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers sx={{ bgcolor: planner.canvas, borderColor: planner.hairline }}>
					{templates.length === 0 ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
							<CircularProgress size={24} sx={{ color: planner.navy }} />
						</Box>
					) : (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
							<Typography sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.inkSecondary }}>
								A set adds the codes you do not have yet; codes you already configured are left
								untouched. Where a suggested role matches one of your job roles, it is set as the
								default.
							</Typography>
							{templates.map((template) => (
								<PlannerCard key={template.key} sx={{ p: 2 }}>
									<Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
										<Box sx={{ minWidth: 0 }}>
											<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>
												{template.name}
											</Typography>
											<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mt: 0.75 }}>
												{template.phases.map((templatePhase) => {
													const role = resolveSuggestedRole(templatePhase.suggestedJobRoleName, jobRoles);
													return (
														<Box
															key={templatePhase.code}
															sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}
														>
															<MonoText sx={{ color: planner.accentDeep, fontSize: 11 }}>
																{templatePhase.code}
															</MonoText>
															<Typography sx={{ ...planner.type.meta, color: planner.inkSecondary }}>
																{templatePhase.name}
															</Typography>
															{templatePhase.suggestedJobRoleName ? (
																<Typography
																	sx={{
																		...planner.type.meta,
																		color: role ? planner.staffed : planner.inkMuted,
																	}}
																>
																	{role ? `→ ${role.name}` : `→ ${templatePhase.suggestedJobRoleName} (no match)`}
																</Typography>
															) : null}
														</Box>
													);
												})}
											</Box>
										</Box>
										<GhostButton
											size="small"
											onClick={() => handleApplyTemplate(template)}
											disabled={applyingTemplateKey !== null}
											sx={{ flexShrink: 0 }}
										>
											{applyingTemplateKey === template.key ? "Applying..." : "Apply"}
										</GhostButton>
									</Box>
								</PlannerCard>
							))}
						</Box>
					)}
				</DialogContent>
			</Dialog>

			{/* Delete confirmation */}
			<Dialog
				open={Boolean(deletingPhase)}
				onClose={() => setDeletingPhase(null)}
				maxWidth="xs"
				fullWidth
				PaperProps={{ sx: dialogPaperSx }}
			>
				<DialogTitle sx={dialogTitleSx}>Delete work phase?</DialogTitle>
				<DialogContent sx={{ bgcolor: planner.surface }}>
					{deleteError ? (
						<Alert severity="error" sx={{ ...alertSx(planner.unstaffed, planner.unstaffedSoft), mb: 1.5 }}>
							{deleteError}
						</Alert>
					) : null}
					<Typography sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.inkSecondary }}>
						{`"${deletingPhase?.code} ${deletingPhase?.name}" will no longer be offered, and imports carrying that code will ask for a job role again.`}
					</Typography>
				</DialogContent>
				<DialogActions sx={{ bgcolor: planner.surface }}>
					<GhostButton onClick={() => setDeletingPhase(null)}>Cancel</GhostButton>
					{/* Destructive confirm — danger status colour, not MUI's error palette.
					    The pressed state darkens the same token rather than adding a shade. */}
					<PrimaryButton
						onClick={handleConfirmDelete}
						disabled={deleting}
						sx={{
							bgcolor: planner.unstaffed,
							borderColor: planner.unstaffed,
							"&:hover": {
								bgcolor: planner.unstaffed,
								borderColor: planner.unstaffed,
								filter: "brightness(0.9)",
							},
						}}
					>
						{deleting ? "Deleting..." : "Delete"}
					</PrimaryButton>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={5000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity || "info"}
					sx={{
						width: "100%",
						borderRadius: `${planner.radiusSm}px`,
						fontFamily: planner.fontUi,
						boxShadow: planner.shadowHover,
						"& .MuiAlert-message": { ...planner.type.body, fontFamily: planner.fontUi },
						...(snackbar.severity === "error"
							? {
								bgcolor: planner.unstaffedSoft,
								border: `1px solid ${planner.unstaffed}33`,
								color: planner.ink,
								"& .MuiAlert-icon": { color: planner.unstaffed },
							}
							: {
								bgcolor: planner.staffedSoft,
								border: `1px solid ${planner.staffed}33`,
								color: planner.ink,
								"& .MuiAlert-icon": { color: planner.staffed },
							}),
					}}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</PlannerPage>
	);
};

export default WorkPhasesAdmin;
